import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, FileText, History, Users, LogOut, Clock, UserCog, FlaskConical, MessageSquare, ExternalLink, Settings as SettingsIcon, HeartPulse, UsersRound, ShieldCheck, FileSignature, ClipboardList, FileCheck, Search, Command } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";
import { Button } from "./ui/button";
import GlobalSearch from "./GlobalSearch";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();

  const items = [
    { to: "/app", icon: LayoutDashboard, label: t("dashboard"), end: true, key: "dashboard" },
    { to: "/app/appointments", icon: CalendarDays, label: t("appointments"), key: "appointments" },
    ...(user?.role === "doctor" ? [{ to: "/app/availability", icon: Clock, label: t("availability"), key: "availability" }] : []),
    ...(user?.role !== "admin" ? [{ to: "/app/messages", icon: MessageSquare, label: t("messages"), key: "messages" }] : []),
    ...(user?.role === "doctor" || user?.role === "admin" ? [{ to: "/app/pacientes", icon: UsersRound, label: "Pacientes", key: "patients" }] : []),
    ...(user?.role === "doctor" || user?.role === "admin" ? [{ to: "/app/historia-clinica", icon: HeartPulse, label: "Historia clínica", key: "clinical-history" }] : []),
    ...(user?.role === "patient" ? [{ to: "/app/mi-historia", icon: HeartPulse, label: "Mi historia", key: "my-history" }] : []),
    ...(user?.role === "patient" ? [{ to: "/app/dependientes", icon: UsersRound, label: "Familiares", key: "dependents" }] : []),
    { to: "/app/prescriptions", icon: FileText, label: t("prescriptions"), key: "prescriptions" },
    { to: "/app/certificados", icon: FileSignature, label: "Certificados", key: "certificates" },
    { to: "/app/informes", icon: FileCheck, label: "Informes", key: "medical-reports" },
    { to: "/app/interrogatorios", icon: ClipboardList, label: "Interrogatorios", key: "interrogations" },
    ...(user?.role !== "admin" ? [{ to: "/app/exams", icon: FlaskConical, label: t("exams"), key: "exams" }] : []),
    ...(user?.role === "doctor" ? [{ to: "/app/profile", icon: UserCog, label: t("profile"), key: "profile" }] : []),
    ...(user?.role === "admin" ? [{ to: "/app/users", icon: Users, label: t("users"), key: "users" }] : []),
    ...(user?.role === "admin" ? [{ to: "/app/settings", icon: SettingsIcon, label: "Ajustes", key: "settings" }] : []),
    ...(user?.role === "admin" ? [{ to: "/app/cumplimiento", icon: ShieldCheck, label: "Cumplimiento", key: "compliance" }] : []),
  ];

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 border-r border-border bg-[#F3F1EC] flex flex-col">
        <div className="px-6 py-5 border-b border-border">
          <Logo size={40} withText />
          <div className="text-xs text-muted-foreground capitalize mt-1">
            {user?.role === "doctor" ? t("doctor") : user?.role === "admin" ? "Admin" : t("patient")}
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-white"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}
          {user?.role === "doctor" && (
            <a
              href="https://app.rempe.es"
              target="_blank"
              rel="noreferrer"
              data-testid="nav-rempe-external"
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-white transition-colors"
            >
              <span className="flex items-center gap-3">
                <ExternalLink className="w-4 h-4" /> Plataforma REMPE
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">↗</span>
            </a>
          )}
        </nav>
        <div className="px-4 py-4 border-t border-border">
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 w-full justify-start text-foreground hover:text-secondary"
            onClick={async () => { await logout(); nav("/"); }}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
          </Button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col">
        <header className="h-16 px-8 border-b border-border bg-card flex items-center justify-between gap-4">
          <div className="font-display text-lg">{t("welcome")}, {user?.name?.split(" ")[0]}</div>
          <div className="flex items-center gap-3">
            {(user?.role === "doctor" || user?.role === "admin") && (
              <button
                onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
                data-testid="open-global-search"
                className="hidden md:flex items-center gap-2 rounded-lg border border-border bg-muted/50 hover:bg-muted px-3 py-1.5 text-sm text-muted-foreground transition-colors"
              >
                <Search className="w-3.5 h-3.5" />
                <span>Buscar…</span>
                <kbd className="inline-flex items-center gap-0.5 rounded border border-border bg-background px-1 py-0.5 text-[10px] font-mono">
                  <Command className="w-2.5 h-2.5" />K
                </kbd>
              </button>
            )}
            <LanguageSwitcher />
          </div>
        </header>
        <div className="flex-1 p-8 overflow-y-auto">{children}</div>
      </main>
      <GlobalSearch enabled={user?.role === "doctor" || user?.role === "admin"} />
    </div>
  );
}
